// =============================================================================
// components/ux/RightClickMenu.tsx
// R2 Right-Click Context Menu — replaces OS contextmenu with R2 Quick Actions.
// Context-aware: different actions based on data-r2-hover element type.
// =============================================================================

import { useEffect, useRef, useState, useCallback, type JSX } from 'react';
import {
  Sparkles, FolderOpen, Clipboard, Camera, PlusCircle, Video,
  Zap, Clock, Bot, Settings, Calculator, Receipt, X,
  ChevronRight, Search, Layers, FileText, Link2, Copy,
  AlignLeft, Reply, ListChecks, CalendarPlus, Reply as ReplyIcon,
  Minus, Star, StarOff,
} from 'lucide-react';
import { useUXControlStore } from '../../stores/uxControl.store.js';
import { favoritesStore } from '../../stores/favorites.store.js';

const ICON_MAP: Record<string, JSX.Element> = {
  Sparkles:    <Sparkles size={13} />,
  FolderOpen:  <FolderOpen size={13} />,
  Clipboard:   <Clipboard size={13} />,
  Camera:      <Camera size={13} />,
  PlusCircle:  <PlusCircle size={13} />,
  Video:       <Video size={13} />,
  Zap:         <Zap size={13} />,
  Clock:       <Clock size={13} />,
  Bot:         <Bot size={13} />,
  Settings:    <Settings size={13} />,
  Calculator:  <Calculator size={13} />,
  Receipt:     <Receipt size={13} />,
  FileText:    <FileText size={13} />,
  Copy:        <Copy size={13} />,
  Search:      <Search size={13} />,
  Link2:       <Link2 size={13} />,
  Layers:      <Layers size={13} />,
  AlignLeft:   <AlignLeft size={13} />,
  Reply:       <Reply size={13} />,
  ListChecks:  <ListChecks size={13} />,
  CalendarPlus: <CalendarPlus size={13} />,
  Minus:       <Minus size={13} />,
};

interface MenuPosition { x: number; y: number }

interface NavPinTarget { id: string; label: string; route: string; }

type EditableTarget = HTMLInputElement | HTMLTextAreaElement | HTMLElement;

function findEditableTarget(el: EventTarget | null): EditableTarget | null {
  let node = el as HTMLElement | null;
  while (node && node !== document.body) {
    const tag = node.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || node.isContentEditable) return node as EditableTarget;
    node = node.parentElement;
  }
  return null;
}

function getEditableSelection(el: EditableTarget | null): string {
  if (!el) return '';
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    return el.value.slice(start, end);
  }
  return window.getSelection()?.toString() ?? '';
}

function insertTextIntoEditable(el: EditableTarget, text: string): void {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    el.value = `${el.value.slice(0, start)}${text}${el.value.slice(end)}`;
    const cursor = start + text.length;
    el.setSelectionRange(cursor, cursor);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    return;
  }
  el.focus();
  document.execCommand('insertText', false, text);
}

function deleteEditableSelection(el: EditableTarget): void {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    el.value = `${el.value.slice(0, start)}${el.value.slice(end)}`;
    el.setSelectionRange(start, start);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    return;
  }
  el.focus();
  document.execCommand('delete');
}

async function copyText(text: string): Promise<void> {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    document.execCommand('copy');
  }
}

/** Walk up the DOM to find a pinnable nav button (data-testid="nav-{route}") */
function findNavPinTarget(el: EventTarget | null): NavPinTarget | null {
  let node = el as HTMLElement | null;
  while (node && node !== document.body) {
    const testId = node.getAttribute?.('data-testid') ?? '';
    if (testId.startsWith('nav-') && !testId.startsWith('nav-group')) {
      const route = testId.slice(4);
      return {
        id:    route,
        label: node.textContent?.trim().replace(/\s+/g, ' ').slice(0, 60) ?? route,
        route,
      };
    }
    node = node.parentElement;
  }
  return null;
}

interface RightClickMenuProps {
  onNavigate: (route: string) => void;
  onAction?: (action: string) => void;
  openCommandPalette?: () => void;
}

export function RightClickMenu({ onNavigate, onAction, openCommandPalette }: RightClickMenuProps): JSX.Element | null {
  const { rightClickEnabled, rightClickCommands } = useUXControlStore();
  const [pos, setPos] = useState<MenuPosition | null>(null);
  const [elementType, setElementType] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [navPin, setNavPin] = useState<NavPinTarget | null>(null);
  const [editableTarget, setEditableTarget] = useState<EditableTarget | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleContextMenu = useCallback((e: MouseEvent) => {
    if (!rightClickEnabled) return;

    // Yield to useDraggableFloat tiles — they handle their own right-click menu
    let floatCheck: HTMLElement | null = e.target as HTMLElement;
    while (floatCheck && floatCheck !== document.body) {
      if (floatCheck.dataset.r2FloatTile) return;
      floatCheck = floatCheck.parentElement;
    }

    const target = e.target as HTMLElement;
    const editable = findEditableTarget(e.target);

    e.preventDefault();
    e.stopPropagation();

    // Detect element context
    let hoverEl: HTMLElement | null = target;
    let detectedType: string | null = null;
    while (hoverEl && hoverEl !== document.body) {
      if (hoverEl.dataset.r2Hover) { detectedType = hoverEl.dataset.r2Hover; break; }
      hoverEl = hoverEl.parentElement;
    }

    setElementType(detectedType);
    setEditableTarget(editable);
    setSelectedText((editable ? getEditableSelection(editable) : window.getSelection()?.toString() ?? '').trim());
    setNavPin(findNavPinTarget(e.target));

    // Auto-flip to stay in viewport
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;
    const MENU_W = 220;
    const MENU_H = 360;
    const x = e.clientX + MENU_W > vpW ? e.clientX - MENU_W : e.clientX;
    const y = e.clientY + MENU_H > vpH ? e.clientY - MENU_H : e.clientY;
    setPos({ x, y });
  }, [rightClickEnabled]);

  const close = useCallback(() => setPos(null), []);

  useEffect(() => {
    document.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('click', close, true);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('click', close, true);
    };
  }, [handleContextMenu, close]);

  const handleAction = useCallback(async (action: string) => {
    close();
    if (action === 'copy-selection') {
      await copyText(selectedText || window.getSelection()?.toString() || '');
      return;
    }
    if (action === 'paste-clipboard' && editableTarget) {
      try {
        const text = await navigator.clipboard.readText();
        insertTextIntoEditable(editableTarget, text);
      } catch { /* clipboard read may be blocked by browser permissions */ }
      return;
    }
    if (action === 'cut-selection' && editableTarget) {
      const text = getEditableSelection(editableTarget);
      await copyText(text);
      deleteEditableSelection(editableTarget);
      return;
    }
    if (action === 'select-all-editable' && editableTarget) {
      editableTarget.focus();
      if (editableTarget instanceof HTMLInputElement || editableTarget instanceof HTMLTextAreaElement) {
        editableTarget.select();
      } else {
        document.execCommand('selectAll');
      }
      return;
    }
    if (action.startsWith('navigate:')) {
      onNavigate(action.replace('navigate:', ''));
      return;
    }
    if (action === 'open-ai') { onNavigate('ai'); return; }
    if (action === 'open-command-palette') { openCommandPalette?.(); return; }
    onAction?.(action);
  }, [close, selectedText, editableTarget, onNavigate, onAction, openCommandPalette]);

  if (!pos) return null;

  const enabledCommands = rightClickCommands
    .filter((c) => c.enabled)
    .sort((a, b) => a.order - b.order);

  // Context-specific extra items
  const contextItems: Array<{ label: string; icon: JSX.Element; action: string; color: string }> =
    editableTarget ? [
      { label: 'Copy', icon: <Copy size={13} />, action: 'copy-selection', color: '#5eead4' },
      { label: 'Paste', icon: <Clipboard size={13} />, action: 'paste-clipboard', color: '#60a5fa' },
      { label: 'Cut', icon: <Minus size={13} />, action: 'cut-selection', color: '#f87171' },
      { label: 'Select All', icon: <AlignLeft size={13} />, action: 'select-all-editable', color: '#a78bfa' },
    ] :
    elementType === 'document' ? [
      { label: 'AI Summary', icon: <Sparkles size={13} />, action: 'ai-summary', color: '#a78bfa' },
      { label: 'Copy Reference', icon: <Copy size={13} />, action: 'copy-ref', color: '#5eead4' },
    ] :
    elementType === 'email' ? [
      { label: 'Summarize Email', icon: <AlignLeft size={13} />, action: 'summarize', color: '#a78bfa' },
      { label: 'Draft Reply', icon: <Reply size={13} />, action: 'draft-reply', color: '#60a5fa' },
    ] :
    elementType === 'event' ? [
      { label: 'Prepare Meeting', icon: <CalendarPlus size={13} />, action: 'prepare-meeting', color: '#5eead4' },
    ] :
    selectedText ? [
      { label: 'Explain Selection', icon: <Sparkles size={13} />, action: 'explain-text', color: '#a78bfa' },
      { label: 'Copy', icon: <Copy size={13} />, action: 'copy-selection', color: '#5eead4' },
    ] : [];

  return (
    <div
      ref={menuRef}
      data-testid="r2-right-click-menu"
      style={{
        position: 'fixed', left: pos.x, top: pos.y,
        zIndex: 99950, width: 220,
        background: 'rgba(3,8,22,0.97)',
        border: '1px solid rgba(37,181,168,0.22)',
        borderRadius: 12,
        backdropFilter: 'blur(28px)',
        WebkitBackdropFilter: 'blur(28px)',
        boxShadow: '0 16px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(37,181,168,0.05)',
        overflow: 'hidden',
        animation: 'rcMenuIn 0.14s cubic-bezier(0.16,1,0.3,1)',
      }}
    >
      {/* Header */}
      <div style={{
        height: 2, background: 'linear-gradient(90deg, #25B5A8, #818cf8, transparent)',
      }} />
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '7px 12px 5px',
        borderBottom: '1px solid rgba(37,181,168,0.08)',
      }}>
        <Sparkles size={10} style={{ color: 'rgba(37,181,168,0.5)' }} />
        <span style={{ fontSize: 9, color: 'rgba(37,181,168,0.5)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: 'monospace' }}>
          R2 QUICK ACTIONS
        </span>
        {elementType && (
          <span style={{
            marginLeft: 'auto', fontSize: 8, padding: '1px 5px', borderRadius: 4,
            background: 'rgba(37,181,168,0.1)', border: '1px solid rgba(37,181,168,0.2)',
            color: '#5eead4',
          }}>
            {elementType}
          </span>
        )}
      </div>

      {/* Nav item: Pin/Unpin to Favorites */}
      {navPin && (
        <div style={{ padding: '4px 6px', borderBottom: '1px solid rgba(37,181,168,0.08)' }}>
          <RCMItem
            label={favoritesStore.has(navPin.id) ? 'Remove from Favorites' : 'Pin to Favorites'}
            icon={favoritesStore.has(navPin.id) ? <StarOff size={13} /> : <Star size={13} />}
            color={favoritesStore.has(navPin.id) ? '#f87171' : '#fcd34d'}
            onClick={() => {
              if (favoritesStore.has(navPin.id)) {
                favoritesStore.remove(navPin.id);
              } else {
                favoritesStore.add({ id: navPin.id, label: navPin.label, route: navPin.route, iconName: 'Star' });
              }
              close();
            }}
          />
        </div>
      )}

      {/* Context-specific items */}
      {contextItems.length > 0 && (
        <div style={{ padding: '4px 6px', borderBottom: '1px solid rgba(37,181,168,0.08)' }}>
          {contextItems.map((item) => (
            <RCMItem
              key={item.action}
              label={item.label}
              icon={item.icon}
              color={item.color}
              onClick={() => handleAction(item.action)}
            />
          ))}
        </div>
      )}

      {/* Main commands */}
      <div style={{ padding: '4px 6px 6px', maxHeight: 280, overflowY: 'auto' }}>
        {/* Command palette shortcut at top */}
        <RCMItem
          label="Open Command Palette"
          icon={<Search size={13} />}
          color="#818cf8"
          shortcut="Ctrl+K"
          onClick={() => { close(); openCommandPalette?.(); }}
        />
        <div style={{ height: 1, background: 'rgba(37,181,168,0.08)', margin: '3px 4px' }} />
        {enabledCommands.map((cmd) => (
          <RCMItem
            key={cmd.id}
            label={cmd.label}
            icon={ICON_MAP[cmd.icon] ?? <ChevronRight size={13} />}
            color="rgba(148,172,210,0.7)"
            onClick={() => handleAction(cmd.action)}
          />
        ))}
      </div>

      <style>{`
        @keyframes rcMenuIn {
          from { opacity: 0; transform: scale(0.94) translateY(-6px); transform-origin: top left; }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

function RCMItem({ label, icon, color, shortcut, onClick }: {
  label: string; icon: JSX.Element; color: string; shortcut?: string; onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 8px', borderRadius: 7, cursor: 'pointer',
        background: 'none', border: 'none', textAlign: 'left',
        transition: 'background 0.08s',
      }}
      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = 'rgba(37,181,168,0.07)')}
      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = 'none')}
    >
      <span style={{ color, flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#c8d8f0' }}>{label}</span>
      {shortcut && (
        <kbd style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, background: 'rgba(30,50,80,0.6)', border: '1px solid rgba(50,70,100,0.4)', color: 'rgba(148,172,210,0.4)', fontFamily: 'monospace' }}>
          {shortcut}
        </kbd>
      )}
    </button>
  );
}
